import { Fragment, type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";

import type { DatasetRandomSampleResponse } from "@prequel/import-react";
import { useRandomSampleDataset, useTestExpression } from "@prequel/import-react";
import {
  Alert,
  AlertKind,
  Button,
  ButtonStyle,
  Dropdown,
  type DropdownListItem,
  RefreshIcon,
  Spinner,
} from "@prequel/shared-components";

import { getErrorMessage } from "@/api/helpers";
import { type DestinationField, formatSchemaType, isJsonObject, parseMaybeJson } from "@/pages/import/recordSchema";

type ExpressionEditorProps = {
  datasetID: string;
  field: DestinationField;
  initialExpression: string;
  onCancel: () => void;
  onInsert: (expression: string) => void;
};

type PreviewState =
  | { status: "idle" }
  | { status: "processing" }
  | { status: "success"; expression: string; result: unknown }
  | { status: "error"; message: string };

type PreviewTab = "shape" | "preview";

const SAMPLE_SIZE = 10;
const EMPTY_SAMPLE_ROWS: unknown[] = [];
const EXPRESSION_OPERATORS = [
  { operator: "+", description: "Addition" },
  { operator: "-", description: "Subtraction" },
  { operator: "*", description: "Multiplication" },
  { operator: "/", description: "Division" },
  { operator: "%", description: "Modulo" },
  { operator: "==", description: "Equals" },
  { operator: "!=", description: "Not equals" },
  { operator: "&&", description: "And" },
  { operator: "||", description: "Or" },
];
const EXPRESSION_FUNCTIONS = [
  "CONCAT",
  "CONCATENATE",
  "IF",
  "LEFT",
  "RIGHT",
  "MID",
  "LEN",
  "FIND",
  "SEARCH",
  "SUBSTITUTE",
  "PROPER",
  "TEXT",
  "VALUE",
  "TIMESTAMPVALUE",
  "TODAY",
  "SHA256",
];

const ExpressionEditor = ({ datasetID, field, initialExpression, onCancel, onInsert }: ExpressionEditorProps) => {
  const { randomSampleDataset: loadSampleRows, sample, isSampling, error: sampleError } = useRandomSampleDataset();
  const { testExpression, isTesting } = useTestExpression();
  const [expression, setExpression] = useState(initialExpression);
  const [selectedSampleIndex, setSelectedSampleIndex] = useState(0);
  const [previewState, setPreviewState] = useState<PreviewState>({ status: "idle" });
  const [previewTab, setPreviewTab] = useState<PreviewTab>(field.type === "object" ? "shape" : "preview");

  const sampleRows = useMemo(() => getSampleRows(sample), [sample]);
  const sampleItems = useMemo<DropdownListItem<string>[]>(
    () =>
      sampleRows.map((_, index) => ({
        key: String(index),
        text: `Sample row ${index + 1}`,
      })),
    [sampleRows],
  );
  const selectedSampleItem = sampleItems.find((item) => item.key === String(selectedSampleIndex));
  const selectedSampleRow = sampleRows[selectedSampleIndex];
  const selectedExtractedData = getExtractedData(selectedSampleRow);
  const canInsert =
    previewState.status === "success" && previewState.expression === expression.trim() && expression.trim().length > 0;

  const refreshSamples = useCallback(async () => {
    if (!datasetID) {
      return;
    }

    setSelectedSampleIndex(0);
    setPreviewState({ status: "idle" });
    try {
      await loadSampleRows({ datasetId: datasetID, sampleSize: SAMPLE_SIZE });
    } catch {
      // The inline alert renders the action error.
    }
  }, [datasetID, loadSampleRows]);

  useEffect(() => {
    setExpression(initialExpression);
    setPreviewState({ status: "idle" });
  }, [initialExpression]);

  useEffect(() => {
    void refreshSamples();
  }, [refreshSamples]);

  const onExpressionChange = (value: string) => {
    setExpression(value);
    setPreviewState({ status: "idle" });
  };

  const onSampleSelected = (item: DropdownListItem<string>) => {
    setSelectedSampleIndex(Number(item.key));
    setPreviewState({ status: "idle" });
  };

  const generatePreview = async () => {
    const expr = expression.trim();
    if (!expr) {
      setPreviewState({ status: "error", message: "Enter an expression to preview." });
      return;
    }

    if (!isJsonObject(selectedExtractedData)) {
      setPreviewState({ status: "error", message: "Select a sample row to preview this expression." });
      return;
    }

    setPreviewState({ status: "processing" });
    try {
      const response = await testExpression({ data: JSON.stringify(selectedExtractedData), expr });
      if (response.error) {
        setPreviewState({ status: "error", message: response.error });
        return;
      }

      setPreviewState({ status: "success", expression: expr, result: response.result });
      setPreviewTab("preview");
    } catch (error) {
      setPreviewState({ status: "error", message: getErrorMessage(error) });
    }
  };

  return (
    <Transition.Root show as={Fragment}>
      <Dialog as="div" className="relative z-40" onClose={onCancel}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-40 overflow-y-auto">
          <div
            className="
              flex min-h-full items-start justify-center p-4 text-left
              sm:items-center
            "
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="my-8 w-full max-w-6xl transform rounded-md bg-white p-5 shadow-xl transition-all">
                <div className="space-y-1">
                  <Dialog.Title className="text-xl font-semibold text-gray-900">Expression editor</Dialog.Title>
                  <p className="text-sm text-gray-500">
                    Write an expression that produces a value for{" "}
                    <code className="rounded-sm bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-800">
                      {field.name}
                    </code>
                    . Reference any column from the source dataset, and use the sample row to preview the output.
                  </p>
                </div>

                <div
                  className="
                    mt-5 grid gap-4
                    lg:grid-cols-2
                  "
                >
                  <Panel title="Expression language">
                    <div className="space-y-4">
                      <div>
                        <h5 className="text-xs font-semibold tracking-wide text-gray-500 uppercase">Arithmetic</h5>
                        <div className="mt-2 space-y-2">
                          {EXPRESSION_OPERATORS.map((row) => (
                            <div key={row.operator} className="grid grid-cols-[4rem_minmax(0,1fr)] gap-3 text-sm">
                              <code className="font-mono text-gray-900">{row.operator}</code>
                              <span className="text-gray-500">{row.description}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h5 className="text-xs font-semibold tracking-wide text-gray-500 uppercase">Functions</h5>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {EXPRESSION_FUNCTIONS.map((name) => (
                            <code
                              key={name}
                              className="rounded-sm bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-700"
                            >
                              {name}
                            </code>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Panel>

                  <Panel title="Expression">
                    <div className="flex h-full flex-col gap-3">
                      <textarea
                        id={`expression-${field.name}`}
                        value={expression}
                        onChange={(event) => onExpressionChange(event.target.value)}
                        className="
                          min-h-44 flex-1 rounded-md border border-gray-300 px-3 py-2 font-mono text-sm text-gray-900
                          shadow-sm
                          focus:border-emerald-500 focus:ring-emerald-500
                        "
                        placeholder="CONCAT(first_name, ' ', last_name)"
                      />
                      <div className="flex justify-end">
                        <Button
                          type={ButtonStyle.TERTIARY}
                          text={
                            isTesting || previewState.status === "processing" ? (
                              <span className="inline-flex items-center">
                                <Spinner.Inline className="mr-2 text-gray-500" />
                                Generating...
                              </span>
                            ) : (
                              "Generate preview"
                            )
                          }
                          onClick={generatePreview}
                          disabled={isTesting || isSampling}
                        />
                      </div>
                    </div>
                  </Panel>

                  <Panel title="Sample row">
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <div className="min-w-0 flex-1">
                          <Dropdown
                            items={sampleItems}
                            selectedItem={selectedSampleItem}
                            setSelectedItem={onSampleSelected}
                            disabled={isSampling || sampleItems.length === 0}
                            isLoading={isSampling}
                            loadingText="Loading sample rows..."
                            emptyText="No sample rows returned."
                          />
                        </div>
                        <button
                          type="button"
                          onClick={refreshSamples}
                          disabled={isSampling}
                          className="
                            inline-flex size-10 items-center justify-center rounded-md border border-gray-300 bg-white
                            text-gray-600
                            hover:bg-gray-50
                            disabled:cursor-not-allowed disabled:opacity-50
                          "
                          title="Refresh sample rows"
                        >
                          <RefreshIcon
                            className={`
                              size-4
                              ${isSampling ? "animate-spin" : ""}
                            `}
                          />
                        </button>
                      </div>
                      {sampleError ? <Alert kind={AlertKind.ERROR}>{getErrorMessage(sampleError)}</Alert> : null}
                      <SampleRowReadout row={selectedSampleRow} />
                    </div>
                  </Panel>

                  <Panel
                    title="Transformation preview"
                    actions={
                      field.type === "object" ? (
                        <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5">
                          <PreviewTabButton active={previewTab === "shape"} onClick={() => setPreviewTab("shape")}>
                            Target shape
                          </PreviewTabButton>
                          <PreviewTabButton active={previewTab === "preview"} onClick={() => setPreviewTab("preview")}>
                            Preview
                          </PreviewTabButton>
                        </div>
                      ) : undefined
                    }
                  >
                    {field.type === "object" && previewTab === "shape" ? (
                      <TargetShape field={field} />
                    ) : (
                      <PreviewOutput state={previewState} />
                    )}
                  </Panel>
                </div>

                <div className="mt-5 flex justify-end gap-3">
                  <Button type={ButtonStyle.TERTIARY} text="Cancel" onClick={onCancel} />
                  <Button
                    type={ButtonStyle.SECONDARY}
                    text="Insert Expression"
                    disabled={!canInsert}
                    onClick={() => onInsert(expression.trim())}
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

const Panel = ({ title, actions, children }: { title: string; actions?: ReactNode; children: ReactNode }) => (
  <section className="flex min-h-72 flex-col rounded-md border border-gray-200 bg-gray-50 p-4">
    <div className="mb-3 flex min-h-8 items-start justify-between gap-3">
      <h4 className="text-xs font-semibold tracking-wide text-gray-500 uppercase">{title}</h4>
      {actions}
    </div>
    <div className="flex-1">{children}</div>
  </section>
);

const PreviewTabButton = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`
      rounded-sm px-2.5 py-1 text-xs font-medium
      ${
        active
          ? "bg-gray-900 text-white"
          : `
            text-gray-600
            hover:bg-gray-100
          `
      }
    `}
  >
    {children}
  </button>
);

const getSampleRows = (sample: DatasetRandomSampleResponse | undefined): unknown[] =>
  sample?.records ?? EMPTY_SAMPLE_ROWS;

const SampleRowReadout = ({ row }: { row: unknown }) => {
  const extractedData = getExtractedData(row);

  if (!isJsonObject(extractedData)) {
    return <p className="text-sm text-gray-500">Load sample rows to inspect source values.</p>;
  }

  return (
    <div className="max-h-72 overflow-auto rounded-md border border-gray-200 bg-white">
      {Object.entries(extractedData).map(([key, value]) => (
        <div
          key={key}
          className="
            grid grid-cols-[minmax(7rem,12rem)_minmax(0,1fr)] gap-4 border-b border-gray-100 px-3 py-2
            last:border-b-0
          "
        >
          <span className="min-w-0 truncate text-sm text-gray-500">{key}</span>
          <code className="min-w-0 overflow-hidden font-mono text-sm wrap-break-word text-gray-900">
            {formatPreviewValue(value)}
          </code>
        </div>
      ))}
    </div>
  );
};

const PreviewOutput = ({ state }: { state: PreviewState }) => {
  if (state.status === "idle") {
    return (
      <p className="text-sm text-gray-500">
        Click Generate preview to evaluate your expression against the selected sample row.
      </p>
    );
  }

  if (state.status === "processing") {
    return (
      <div className="flex items-center text-sm text-gray-600">
        <Spinner.Inline className="mr-2 text-gray-500" />
        Evaluating expression...
      </div>
    );
  }

  if (state.status === "error") {
    return <Alert kind={AlertKind.ERROR}>{state.message}</Alert>;
  }

  return (
    <pre className="max-h-72 overflow-auto p-3 font-mono text-sm text-gray-900">{formatPreviewValue(state.result)}</pre>
  );
};

const TargetShape = ({ field }: { field: DestinationField }) => (
  <div className="space-y-3">
    <div className="max-h-72 overflow-auto rounded-md border border-gray-200 bg-white p-3">
      <SchemaShape schema={field.schema} />
    </div>
    <p className="text-xs text-gray-500">
      Craft an expression whose output matches this shape. Required fields are highlighted. Optional fields render in
      grey.
    </p>
  </div>
);

const SchemaShape = ({ schema }: { schema: unknown }) => {
  if (!isJsonObject(schema) || !isJsonObject(schema.properties)) {
    return <code className="font-mono text-sm text-orange-700">{`<${formatSchemaType(schema)}>`}</code>;
  }

  const required = Array.isArray(schema.required)
    ? new Set(schema.required.filter((field): field is string => typeof field === "string"))
    : new Set<string>();

  return (
    <div className="space-y-1 font-mono text-sm">
      <div>{"{"}</div>
      <div className="space-y-1 pl-4">
        {Object.entries(schema.properties).map(([name, property]) => {
          const isRequired = required.has(name);
          return (
            <div key={name}>
              <span className="text-gray-900">{name}: </span>
              <span
                className={isRequired ? "text-orange-700" : "text-gray-500"}
              >{`<${formatSchemaType(property)}>`}</span>
            </div>
          );
        })}
      </div>
      <div>{"}"}</div>
    </div>
  );
};

const getExtractedData = (row: unknown): unknown => {
  if (!isJsonObject(row)) {
    return undefined;
  }

  return parseMaybeJson(row.extracted_data);
};

const formatPreviewValue = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }

  return JSON.stringify(value, null, 2) ?? "";
};

export default ExpressionEditor;
